import axios from 'axios';
import { showError } from '@/utils/errorHandler';

const GITEE_API_URL = process.env.VUE_APP_GITEE_API_URL || 'https://gitee.com/api/v5';
const DEFAULT_TIMEOUT = 8000;
export const DEFAULT_GITEE_OWNER = 'java-and-net';

const giteeService = axios.create({
  baseURL: GITEE_API_URL,
  timeout: DEFAULT_TIMEOUT,
});

const pendingRequests = new Map();

giteeService.interceptors.request.use(
  (config) => {
    const accessTokenFromEnv = process.env.VUE_APP_GITEE_ACCESS_TOKEN;
    const accessTokenFromLocal = localStorage.getItem('gitee_access_token');
    const accessTokenFromConfig = config.accessToken; // custom field for passing token
    const accessToken = accessTokenFromConfig || accessTokenFromLocal || accessTokenFromEnv;

    // ensure params exists
    config.params = config.params || {};
    if (accessToken && !config.params.access_token) {
      config.params.access_token = accessToken;
    }

    const requestKey = `${config.method}:${config.baseURL}${config.url}`;
    if (pendingRequests.has(requestKey)) {
      showError('重复请求被阻止：' + requestKey);
      return Promise.reject(new Error('Duplicate request'));
    }
    pendingRequests.set(requestKey, true);
    return config;
  },
  (error) => Promise.reject(error)
);

giteeService.interceptors.response.use(
  (response) => {
    const requestKey = `${response.config.method}:${response.config.baseURL}${response.config.url}`;
    pendingRequests.delete(requestKey);
    return response.data;
  },
  (error) => {
    if (error && error.config) {
      const requestKey = `${error.config.method}:${error.config.baseURL}${error.config.url}`;
      pendingRequests.delete(requestKey);
    }
    if (error.code === 'ECONNABORTED') {
      showError('Gitee 请求超时');
    } else if (error.response) {
      const { status, data } = error.response;
      if (status === 401) {
        showError('Gitee认证失败或Token无效');
      } else if (status === 403) {
        showError('Gitee权限不足：' + (data && data.message ? data.message : 'Forbidden'));
      } else if (status >= 500) {
        showError('Gitee服务端错误');
      } else {
        showError('Gitee响应错误：' + (data && data.message ? data.message : '未知错误'));
      }
    } else if (error.request) {
      showError('Gitee网络错误或无响应');
    } else {
      showError('Gitee请求错误：' + error.message);
    }
    return Promise.reject(error);
  }
);

// 通用请求封装
export function requestGitee({ method = 'get', url, params = {}, data = {}, headers = {}, accessToken } = {}) {
  if (!url) throw new Error('url is required');
  return giteeService.request({
    method,
    url,
    params,
    data,
    headers,
    accessToken, // custom
  });
}

// 获取仓库的所有提交
// GET /repos/{owner}/{repo}/commits
// 支持参数：sha, path, author, since, until, page, per_page, access_token
export function getRepoCommits(
  repo,
  {
    owner = DEFAULT_GITEE_OWNER,
    sha,
    path,
    author,
    since,
    until,
    page = 1,
    per_page = 20,
    access_token,
  } = {}
) {
  if (!repo) throw new Error('repo 为必填参数');
  const url = `/repos/${owner}/${repo}/commits`;

  // 规范 per_page 取值范围 1..100
  let pp = per_page;
  if (typeof pp === 'number') {
    pp = Math.min(Math.max(1, pp), 100);
  }

  const params = { page, per_page: pp };
  if (sha) params.sha = sha;
  if (path) params.path = path;
  if (author) params.author = author;
  if (since) params.since = since;   // ISO 8601
  if (until) params.until = until;   // ISO 8601

  return requestGitee({
    method: 'get',
    url,
    params,
    accessToken: access_token,
  });
}

// 获取仓库的开源许可协议
// GET /repos/{owner}/{repo}/license
// 支持参数：access_token
export function getRepoLicense(
  repo,
  {
    owner = DEFAULT_GITEE_OWNER,
    access_token,
  } = {}
) {
  if (!repo) throw new Error('repo 为必填参数');
  const url = `/repos/${owner}/${repo}/license`;

  return requestGitee({
    method: 'get',
    url,
    accessToken: access_token,
  });
}
// 用法示例：
// getRepoCommits('TopskyHotelManagementSystem', { page: 1, per_page: 20 })
//   .then(list => console.log(list))
//   .catch(err => console.error(err));

// 获取仓库代码语言字节数
// GET /repos/{owner}/{repo}/languages
// 支持参数：access_token
export function getRepoLanguages(
  repo,
  {
    owner = DEFAULT_GITEE_OWNER,
    access_token,
  } = {}
) {
  if (!repo) throw new Error('repo 为必填参数');
  const url = `/repos/${owner}/${repo}/languages`;

  return requestGitee({
    method: 'get',
    url,
    accessToken: access_token,
  });
}

